import { Page, Tabs } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import { useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { useLocation } from 'react-use';

import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppNavigate } from 'utils/url/useAppNavigate';

type SettingsAgentsTab = 'agents' | 'workloads' | 'roles';

export const SettingsAgentsView = (): JSX.Element => {
  const navigate = useAppNavigate();
  const location = useLocation();
  const flags = useFeatureFlag(['custom-roles']);

  const hasManageWorkloadsAccess = useHasAccess('settings.workload');
  const hasManageRolesAccess = useHasAccess('organization.access', {
    fallbackRole: 'admin',
  });
  const hasRolesView = flags.customRoles && hasManageRolesAccess;

  const settingsAgentsTabs = useMemo(() => {
    const tabs: { label: string; id: SettingsAgentsTab }[] = [
      { label: 'Agents', id: 'agents' },
    ];

    if (hasManageWorkloadsAccess) {
      tabs.push({ label: 'Workloads', id: 'workloads' });
    }
    if (hasRolesView) {
      tabs.push({ label: 'Roles', id: 'roles' });
    }

    return tabs;
  }, [hasManageWorkloadsAccess, hasRolesView]);

  const handleTabChange = (value: string) => {
    const tab = value as SettingsAgentsTab;

    if (tab === 'agents') {
      navigate({ path: '/settings/agents' });
    } else {
      navigate({ path: `/settings/agents/${tab}` });
    }

    ampli.settingsAgentsTabClicked({ tab });
  };

  const selectedTab = (location.pathname?.split('/').pop() ??
    'agents') as SettingsAgentsTab;

  return (
    <Page>
      <Page.Head title="Agents" />

      {settingsAgentsTabs.length > 1 && (
        <Page.Tabs>
          <Tabs
            tabs={settingsAgentsTabs}
            selectedId={selectedTab}
            onChange={handleTabChange}
          />
        </Page.Tabs>
      )}

      <Page.Body>
        <Outlet />
      </Page.Body>
    </Page>
  );
};
